// Per-run state that persists across scenes.
// Champions are ordered by slot (0 = frontline, 4 = backline).
// unit.pos uses slot-coords: {q: slot, r: 0=player / 1=enemy}.

import type { ChampionId, ChampionTier } from "../data/champions";
import { STARTER_CHAMPIONS, CHAMPIONS } from "../data/champions";
import type { ItemId } from "../data/items";
import { CampaignMap } from "../map/CampaignMap";
import type { MapNode } from "../map/CampaignMap";
import { levelForXp, effectiveStatsForLevel, XP_FOR_LEVEL, MAX_LEVEL } from "../units/UnitStats";
import type { HexCoord } from "../engine/hex/HexCoord";
import type { Unit } from "../units/Unit";
import type { Difficulty } from "../data/difficulty";

export type StarLevel = 1 | 2 | 3 | 4;

function getStarMult(tier: ChampionTier, stars: StarLevel): number {
  const mults: Record<ChampionTier, [number, number, number, number]> = {
    common:    [1, 1.5, 2.0, 3.0],
    rare:      [1, 1.7, 2.6, 4.0],
    epic:      [1, 2.0, 3.2, 5.0],
    legendary: [1, 2.0, 3.8, 6.0],
  };
  return (mults[tier] ?? mults.common)[stars - 1] ?? 1;
}

export interface LevelUpBonuses {
  hpMult: number;
  adMult: number;
}

const DEFAULT_BONUSES: LevelUpBonuses = { hpMult: 1, adMult: 1 };

// pos.q = slot (0-2, 0=frontline), pos.r = 0 (player team)
export interface RosterEntry {
  id: string;
  championId: ChampionId;
  xp: number;
  stars: StarLevel;
  currentHp: number | null;
  pos: HexCoord; // {q: slot, r: 0}
  bonuses: LevelUpBonuses;
  itemId: ItemId | null;
  nasusStacks?: number; // Nasus: flat AD bonus accrued across the run (+1 AD per stack)
  redBuff?: boolean;   // won Red Buff camp — +50% AD, 15% omnivamp (heal on any damage dealt)
  blueBuff?: boolean;  // won Blue Buff camp — every ability is cast twice
}

export interface PendingCombat {
  node: MapNode;
  enemies: Unit[];
  fighterLimit: number | null;
  enemyChampionIds?: ChampionId[];
}

function computeMaxHp(entry: RosterEntry): number {
  const champ = CHAMPIONS[entry.championId];
  const level = levelForXp(entry.xp);
  const sm = getStarMult(champ.tier, entry.stars ?? 1);
  const b = entry.bonuses ?? { hpMult: 1, adMult: 1 };
  return effectiveStatsForLevel(
    { ...champ, baseHp: Math.round(champ.baseHp * sm * b.hpMult), baseAd: Math.round(champ.baseAd * sm), baseAp: Math.round(champ.baseAp * sm) },
    level,
    champ.levelGrowth,
  ).maxHp;
}

export class RunState {
  roster: RosterEntry[] = [];
  map: CampaignMap | null = null;
  seed: number;
  cheatMode: boolean;
  difficulty: Difficulty;
  act: number = 1;
  combatsCompleted: number = 0;
  recruitsCompleted: number = 0;
  pendingCombat: PendingCombat | null = null;
  combatFighterIds: string[] | null = null;
  pendingRecruitOffers: ChampionId[] | null = null;
  pendingItemOffers: string[] | null = null;
  pendingRandomEventNodeId: string | null = null;
  pendingRandomEventType: string | null = null;

  constructor(seed: number, cheatMode = false, difficulty: Difficulty = "normal") {
    this.seed = seed;
    this.cheatMode = cheatMode;
    this.difficulty = difficulty;
  }

  static newRunWithStarterRoster(seed: number, starters = STARTER_CHAMPIONS, cheatMode = false, difficulty: Difficulty = "normal"): RunState {
    const state = new RunState(seed, cheatMode, difficulty);
    for (let i = 0; i < starters.length; i++) {
      state.roster.push({
        id: `roster_${i}`,
        championId: starters[i],
        xp: cheatMode ? XP_FOR_LEVEL[MAX_LEVEL - 1] : 0,
        stars: 1,
        currentHp: null,
        pos: { q: i, r: 0 },
        bonuses: { ...DEFAULT_BONUSES },
        itemId: null,
      });
    }
    return state;
  }

  grantXpToAll(amount: number): void {
    for (const entry of this.roster) {
      if (entry.currentHp !== null && entry.currentHp <= 0) continue;
      entry.xp += amount;
    }
  }

  grantXpTo(rosterId: string, amount: number): void {
    const entry = this.roster.find((r) => r.id === rosterId);
    if (entry) entry.xp += amount;
  }

  levelOf(entry: RosterEntry): number {
    return levelForXp(entry.xp);
  }

  addChampion(championId: ChampionId, startingXp = 0, force = false): boolean {
    if (!force && this.roster.length >= 3) return false;
    if (!CHAMPIONS[championId]) return false;

    this.compactSlots();

    const isMelee = CHAMPIONS[championId].range <= 1;
    // Insert melee after the last melee unit; insert ranged at the end.
    const sortedBySlot = [...this.roster].sort((a, b) => a.pos.q - b.pos.q);
    let insertSlot: number;
    if (isMelee) {
      const lastMeleeIdx = sortedBySlot.reduce<number>(
        (acc, e, i) => (CHAMPIONS[e.championId].range <= 1 ? i : acc),
        -1,
      );
      insertSlot = lastMeleeIdx + 1;
    } else {
      insertSlot = sortedBySlot.length;
    }

    // Shift all champions at insertSlot and beyond one step back.
    for (const e of this.roster) {
      if (e.pos.q >= insertSlot) e.pos = { q: e.pos.q + 1, r: 0 };
    }

    this.roster.push({
      id: `roster_${this.roster.length}_${Date.now()}`,
      championId,
      xp: this.cheatMode ? XP_FOR_LEVEL[MAX_LEVEL - 1] : startingXp,
      stars: 1,
      currentHp: null,
      pos: { q: insertSlot, r: 0 },
      bonuses: { ...DEFAULT_BONUSES },
      itemId: null,
    });

    this.compactSlots();
    return true;
  }

  // Compact slots by preserving current pos.q order without reshuffling roles.
  private compactSlots(): void {
    const sorted = [...this.roster].sort((a, b) => a.pos.q - b.pos.q);
    for (let i = 0; i < sorted.length; i++) {
      sorted[i].pos = { q: i, r: 0 };
    }
  }

  // Full reorder by role — only used at run initialisation (newRunWithStarterRoster).
  sortRosterByRole(): void {
    const melee  = this.roster.filter((e) => CHAMPIONS[e.championId].range <= 1);
    const ranged = this.roster.filter((e) => CHAMPIONS[e.championId].range >  1);
    const ordered = [...melee, ...ranged];
    for (let i = 0; i < ordered.length; i++) {
      ordered[i].pos = { q: i, r: 0 };
    }
  }

  tryMerge(championId: ChampionId): void {
    const all = this.roster.filter((r) => r.championId === championId);

    const twoStar = all.find((r) => r.stars === 2);
    const oneStar = all.find((r) => r.stars === 1);
    if (twoStar && oneStar) {
      const oldMax = computeMaxHp(twoStar);
      const missing = twoStar.currentHp !== null ? oldMax - twoStar.currentHp : 0;
      twoStar.stars = 3;
      if (twoStar.currentHp !== null) {
        twoStar.currentHp = Math.max(0, computeMaxHp(twoStar) - missing);
      }
      this.roster = this.roster.filter((r) => r.id !== oneStar.id);
      this.compactSlots();
      return;
    }

    const oneStars = all.filter((r) => r.stars === 1);
    if (oneStars.length >= 2) {
      const oldMax = computeMaxHp(oneStars[0]);
      const missing = oneStars[0].currentHp !== null ? oldMax - oneStars[0].currentHp : 0;
      oneStars[0].stars = 2;
      if (oneStars[0].currentHp !== null) {
        oneStars[0].currentHp = Math.max(0, computeMaxHp(oneStars[0]) - missing);
      }
      this.roster = this.roster.filter((r) => r.id !== oneStars[1].id);
      this.tryMerge(championId);
      return;
    }

    this.compactSlots();
  }

  // Swap two roster entries' slots.
  swapSlots(idA: string, idB: string): boolean {
    const a = this.roster.find((r) => r.id === idA);
    const b = this.roster.find((r) => r.id === idB);
    if (!a || !b) return false;
    const tmp = a.pos.q;
    a.pos = { q: b.pos.q, r: 0 };
    b.pos = { q: tmp, r: 0 };
    return true;
  }

  // Move a roster entry to a new slot (0-2).
  moveToSlot(rosterId: string, targetSlot: number): boolean {
    const entry = this.roster.find((r) => r.id === rosterId);
    if (!entry) return false;
    if (targetSlot < 0 || targetSlot > 2) return false;
    const occupant = this.roster.find((r) => r.id !== rosterId && r.pos.q === targetSlot);
    if (occupant) {
      occupant.pos = { q: entry.pos.q, r: 0 };
    }
    entry.pos = { q: targetSlot, r: 0 };
    return true;
  }

  healFull(): void {
    for (const entry of this.roster) entry.currentHp = null;
  }

  // Permanently remove a champion from the roster and compact slots.
  removeFromRoster(rosterId: string): boolean {
    const before = this.roster.length;
    this.roster = this.roster.filter((r) => r.id !== rosterId);
    if (this.roster.length === before) return false;
    this.compactSlots();
    return true;
  }

  isRosterFull(): boolean {
    return this.roster.length >= 3;
  }

  private nextFreeSlot(): number {
    const occupied = new Set(this.roster.map((e) => e.pos.q));
    for (let s = 0; s < 3; s++) {
      if (!occupied.has(s)) return s;
    }
    return 2;
  }
}

const SAVE_KEY = "lolRogue_save";

export function saveRun(state: RunState): void {
  if (!state.map) return;
  const data = {
    roster: state.roster,
    seed: state.seed,
    cheatMode: state.cheatMode,
    difficulty: state.difficulty,
    act: state.act,
    combatsCompleted: state.combatsCompleted,
    recruitsCompleted: state.recruitsCompleted,
    pendingRecruitOffers: state.pendingRecruitOffers,
    pendingItemOffers: state.pendingItemOffers,
    pendingRandomEventNodeId: state.pendingRandomEventNodeId,
    pendingRandomEventType: state.pendingRandomEventType,
    map: {
      nodes: state.map.nodes,
      edges: state.map.edges,
      currentNodeId: state.map.currentNodeId,
      completedNodeIds: [...state.map.completedNodeIds],
    },
  };
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(data)); } catch {}
}

export function clearSave(): void {
  try { localStorage.removeItem(SAVE_KEY); } catch {}
}

export function loadSavedRun(): RunState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Migrate legacy `impossibleMode: true` saves into the new "hard" difficulty.
    const difficulty: Difficulty =
      data.difficulty ?? (data.impossibleMode ? "hard" : "normal");
    const state = new RunState(data.seed, data.cheatMode ?? false, difficulty);
    state.roster = data.roster;
    state.act = data.act ?? 1;
    state.combatsCompleted = data.combatsCompleted ?? 0;
    state.recruitsCompleted = data.recruitsCompleted ?? 0;
    state.pendingRecruitOffers = data.pendingRecruitOffers ?? null;
    state.pendingItemOffers = data.pendingItemOffers ?? null;
    state.pendingRandomEventNodeId = data.pendingRandomEventNodeId ?? null;
    state.pendingRandomEventType = data.pendingRandomEventType ?? null;
    if (data.map) {
      state.map = new CampaignMap(
        data.map.nodes,
        data.map.edges,
        data.map.currentNodeId,
        new Set(data.map.completedNodeIds),
      );
    }
    return state;
  } catch {
    return null;
  }
}

export function hasSavedRun(): boolean {
  try { return !!localStorage.getItem(SAVE_KEY); } catch { return false; }
}

// Legacy helper: check if pos is player-team slot (r === 0)
export function isPlayerZone(pos: HexCoord): boolean {
  return pos.r === 0;
}
